import { useCallback, useMemo, useState } from "react"
import { Box, MenuItem } from "@mui/material"
import { MRT_ColumnDef, MRT_Row, MRT_TableInstance } from "material-react-table"
import { enqueueSnackbar } from "notistack"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"

import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { getEditProductGroupRoute } from "../../../../../constants/routes"
import {
    deleteProductGroup,
    fetchProductGroups,
    FetchProductGroupsResponse,
    ProductGroupQuery,
} from "../productGroupApi"
import { PriceListQuery, sortProductGroupInPriceList } from "../../priceList/priceListApi"
import { EnableProductSort } from "../../priceList/components/EnableProductSort"
import { ProductGroupItem } from "../productGroupTypes"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { makeStyles } from "tss-react/mui"
import { ProductGroupInfoIconsStandalone } from "../../products/components/tableElements/ProductInfoIcons"
import { useOptimisticOrder } from "../../products/hooks/useOptimisticOrder"
import { GROW_CELL_CLASS, useGridLayoutStyles } from "../../../../../shared/components/table/useGridLayoutStyles"
import { COLUMN_SIZE } from "../../../../../shared/constants/table/config"

const useStyles = makeStyles()((theme) => ({
    deleteItem: {
        color: theme.palette.tailwind.red[600],
    },
    iconRow: {
        marginTop: ".25rem",
    },
    topToolbar: {
        alignItems: "flex-start",
    },
    toolbarStack: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: ".75rem",
        paddingBottom: ".5rem",
    },
    // Reveal the drag (Move) handle only when hovering the row.
    dragHandleRow: {
        "& .product-group-drag-cell .MuiButtonBase-root": {
            opacity: 0,
            transition: "opacity .15s",
        },
        "&:hover .product-group-drag-cell .MuiButtonBase-root": {
            opacity: 1,
        },
    },
}))

const DISPLAY_COLUMN_DEF_OPTIONS = {
    "mrt-row-drag": {
        header: "",
        size: COLUMN_SIZE.XS,
        maxSize: COLUMN_SIZE.XS,
        muiTableBodyCellProps: {
            align: "center" as const,
            className: "product-group-drag-cell",
        },
    },
} as const

function transformData(data: FetchProductGroupsResponse) {
    return data.productGroups
}

/** Stable MRT row id so group rows keep identity across reorders. */
const getProductGroupRowId = (row: ProductGroupItem): string => String(row.id)

/** Move the dragged group to the target group's position (pure, for optimistic reorder). */
function reorderGroups(
    groups: ProductGroupItem[],
    draggingItem: ProductGroupItem,
    targetItem: ProductGroupItem,
): ProductGroupItem[] {
    const fromIndex = groups.findIndex((group) => group.id === draggingItem.id)
    const toIndex = groups.findIndex((group) => group.id === targetItem.id)

    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return groups
    }

    const next = [...groups]
    const [moved] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, moved)

    return next
}

type AllProductGroupsTabProps = Readonly<{
    priceListId: number
    isResetProductSort: boolean
    toolbarLeftContent?: React.ReactNode
}>

export function AllProductGroupsTab({ priceListId, isResetProductSort, toolbarLeftContent }: AllProductGroupsTabProps) {
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()
    const { classes } = useStyles()
    const { classes: gridClasses } = useGridLayoutStyles()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [confirmModalText, setConfirmModalText] = useState("")
    const [productGroupId, setProductGroupId] = useState<number>()

    const productGroups = useApiQuery(fetchProductGroups, {
        queryName: ProductGroupQuery.LIST,
        dependencies: {
            merchantId,
            priceListId,
        },
        select: transformData,
    })

    // Optimistic local ordering so a group drag shows instantly; rolls back on failure.
    const { orderedRows, applyOptimisticMove, rollback } = useOptimisticOrder(productGroups.data, reorderGroups)

    const { ApiQueryTable, columnFactory } = useApiQueryTable({
        data: orderedRows,
        isInitialLoading: productGroups.isInitialLoading,
        isFetching: productGroups.isFetching,
        isError: productGroups.isError,
    })

    const columns = useMemo<MRT_ColumnDef<ProductGroupItem>[]>(
        () => [
            columnFactory.shortNameColumn({
                header: l10n.getString("product-group-code-column-header"),
                id: "productGroupCode",
                accessorFn: (row) => row.productGroupCode?.number ?? "",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("product-group-name-column-header"),
                accessorKey: "name",
                muiTableHeadCellProps: { className: GROW_CELL_CLASS },
                muiTableBodyCellProps: { className: GROW_CELL_CLASS },
                Cell: ({ row }) => (
                    <Box>
                        <Box>{row.original.name}</Box>
                        <Box className={classes.iconRow}>
                            <ProductGroupInfoIconsStandalone
                                availabilityDetails={row.original.availabilityDetails}
                                securitySettings={row.original.securitySettings}
                            />
                        </Box>
                    </Box>
                ),
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("product-group-status-column-header"),
                },
                (row) => row.isActive,
            ),
        ],
        [columnFactory, l10n, classes.iconRow],
    )

    const { mutateAsync: callDeleteProductGroup } = useApiMutation(deleteProductGroup, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ProductGroupQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    // Sort is applied optimistically on drop. On failure: roll back to the pre-move
    // order, then re-throw so the API error is surfaced as a snackbar.
    const { mutate: callSortProductGroup } = useApiMutation(sortProductGroupInPriceList, {
        async onError(_, error) {
            rollback()
            throw error
        },
    })

    const allowSort = isResetProductSort

    const handleDragEnd = useCallback(
        (table: MRT_TableInstance<ProductGroupItem>) => {
            const { draggingRow, hoveredRow } = table.getState()
            if (!draggingRow || !hoveredRow) return

            const targetRow = hoveredRow as MRT_Row<ProductGroupItem>

            // Show the new order immediately (snapshots internally for rollback).
            applyOptimisticMove(draggingRow.original, targetRow.original)

            callSortProductGroup({
                priceListId,
                productGroupId: draggingRow.original.id,
                body: {
                    moveTo: { position: targetRow.index + 1 },
                },
            })
        },
        [priceListId, applyOptimisticMove, callSortProductGroup],
    )

    const dragHandleProps = useCallback(
        ({ table }: { table: MRT_TableInstance<ProductGroupItem> }) => ({
            onDragEnd: () => handleDragEnd(table),
        }),
        [handleDragEnd],
    )

    const enableSortAction = !isResetProductSort ? (
        <EnableProductSort
            priceListId={priceListId}
            onEnabled={() => {
                queryClient.invalidateQueries({ queryKey: [PriceListQuery.LIST] })
                queryClient.invalidateQueries({ queryKey: [ProductGroupQuery.LIST] })
            }}
        />
    ) : null

    return (
        <>
            <ApiQueryTable
                enableStickyHeader
                columns={columns}
                layoutMode="grid"
                muiTablePaperProps={{ className: gridClasses.pinned }}
                muiTopToolbarProps={{ className: classes.topToolbar }}
                memoMode="cells"
                getRowId={getProductGroupRowId}
                enableRowOrdering={allowSort}
                displayColumnDefOptions={DISPLAY_COLUMN_DEF_OPTIONS}
                muiTableBodyRowProps={{ className: classes.dragHandleRow }}
                muiTableBodyRowDragHandleProps={dragHandleProps}
                renderTopToolbarCustomActions={() => (
                    <div className={classes.toolbarStack}>
                        {toolbarLeftContent}
                        {enableSortAction}
                    </div>
                )}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="edit" key="edit">
                        <MenuItem
                            onClick={() => {
                                navigate(getEditProductGroupRoute(row.original.id))
                                closeMenu()
                            }}
                        >
                            Edit
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="delete" key="delete">
                        <MenuItem
                            className={classes.deleteItem}
                            onClick={() => {
                                setShowConfirmModal(true)
                                setConfirmModalText(row.original.name)
                                setProductGroupId(row.original.id)
                                closeMenu()
                            }}
                        >
                            Delete
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
            />

            {showConfirmModal && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("product-group-delete")}
                    entityName={confirmModalText}
                    handleClick={() => {
                        if (productGroupId) {
                            callDeleteProductGroup({ productGroupId, merchantId: merchantId ?? 0 })
                        } else {
                            throw new Error("Product group id is undefined")
                        }
                    }}
                    onClose={() => setShowConfirmModal(false)}
                    open={showConfirmModal}
                />
            )}
        </>
    )
}
